import { styled } from "styled-components";

import { useChatClass } from "../../theme";
import { Container, BaseButton } from "./styled";

interface ButtonProps extends React.ComponentProps<typeof Container> {
  /** Type of button  */
  variant: "info" | "warn" | "subtle" | "outline";
  /** Visual size of the button (controls height). Default inherits prior styles. */
  size?: "sm" | "md" | "lg" | "xl";
  /** CSS width value (e.g., "100%", "auto"). Default is auto. */
  width?: string;
}

interface ButtonComponent extends React.FC<ButtonProps> {
  Primary: typeof PrimaryButton;
  Secondary: typeof SecondaryButton;
  Container: typeof Container;
  BaseButton: typeof BaseButton;
}

/** Basic button */
const Button: ButtonComponent = ({
  variant = "info",
  size,
  width,
  ...props
}: ButtonProps) => {
  const chatClass = useChatClass();
  if (variant === "outline") {
    return (
      <SecondaryButton
        $size={size}
        $width={width}
        {...props}
        className={chatClass("button", variant)}
      />
    );
  } else {
    return (
      <PrimaryButton
        $variant={variant}
        $size={size}
        $width={width}
        {...props}
        className={chatClass("button", variant)}
      />
    );
  }
};

const PrimaryButton = styled(Container)<PrimaryButtonProps>(({
  $variant = "info",
  $size,
  $width,
  theme,
}) => {
  let style;

  switch ($variant) {
    case "info":
      style = {
        backgroundColor: theme.colors.primary,
        "&:hover": {
          backgroundColor: theme.colors.primaryDark,
        },
      };
      break;
    case "warn":
      style = {
        backgroundColor: theme.colors.warn,
        "&:hover": {
          backgroundColor: theme.colors.warnDark,
        },
      };
      break;
    case "subtle":
      style = {
        color: theme.colors.black,
        backgroundColor: "inherit",
        transition: "color",
        "&:hover": {
          color: "#000",
          backgroundColor: "rgba(0,0,0,0.08)",
        },
      };
      break;
    default:
      style = {};
  }

  const height =
    $size === "sm"
      ? theme.sizes.sm
      : $size === "xl"
        ? theme.sizes.xl
        : $size === "lg"
          ? theme.sizes.lg
          : theme.sizes.md;

  return {
    height,
    width: $width || "auto",
    color: theme.colors.white,
    transition: "background-color",
    ...style,
  };
});

type PrimaryButtonProps = {
  /** Default is "info" */
  $variant?: "info" | "warn" | "subtle";
  /** Size controls height */
  $size?: "sm" | "md" | "lg" | "xl";
  /** CSS width */
  $width?: string;
};

const SecondaryButton = styled(Container)<{
  $size?: "sm" | "md" | "lg" | "xl";
  $width?: string;
}>(({ $size, $width, theme }) => ({
  height:
    $size === "xl"
      ? theme.sizes.xl
      : $size === "md"
        ? theme.sizes.md
        : $size === "lg"
          ? theme.sizes.lg
          : theme.sizes.sm,
  width: $width || "auto",
  border: `1px solid ${theme.colors.primaryFaded}`,
  color: theme.colors.primary,
  backgroundColor: theme.colors.white,
  boxShadow: `0 1px 2px ${theme.shadows["2"]}`,
  transition: "border-color",

  "&:hover": {
    borderColor: theme.colors.primary,
  },
}));

Button.Container = Container;
Button.BaseButton = BaseButton;
Button.Primary = PrimaryButton;
Button.Secondary = SecondaryButton;

export { Button };
